/**
 * Mandatory photo evidence — the shots a submission MUST carry before it can
 * be approved for upload (QA rules `hardware-photos` and `checklist-photos`
 * in backend/src/qa).
 *
 * Source: technical-head feedback on the M2 QA scope — four hardware shots
 * plus five checklist-item-referenced signage/labelling shots on the battery
 * side, mirrored onto the PV checklist's own prompt keys.
 *
 * Detection model: FieldPhoto.promptKey is the join key. Standard shots use
 * the photo-prompt library keys (photo-prompts.ts); clause-bound evidence
 * uses the `clause:<checklistItemId>` convention (see FieldPhotoSchema).
 * Each entry lists every promptKey that satisfies it; `subject` mirrors the
 * PWA's photo-prompt subject grouping for display.
 *
 * SCOPING — every entry declares the checklist sections it is mandatory for,
 * and consumers MUST select through `mandatoryHardwarePhotosFor` /
 * `mandatoryChecklistPhotosFor`. An unscoped requirement is unsatisfiable:
 * `getPromptsForJob` never offers a battery prompt on a PV job, and a
 * Section 4 checklist has no `Pre-assembled BS` clauses — so demanding the
 * battery set of every submission fails QA on evidence the inspector had no
 * way to capture. See packages/backend/src/qa/field-parity.test.ts.
 */
import { z } from 'zod';
import { ChecklistSectionSchema } from './field.js';
import { GENERAL_RECOMMENDATIONS_PHOTO_PROMPT_KEY } from './field.js';
import { canonicalPromptKey } from './portal-photo-targets.js';
export const MandatoryPhotoSchema = z.object({
    /** Stable id, used in QA rule detail messages. */
    key: z.string().min(1),
    /** Human label for QA rows / admin UI. */
    label: z.string().min(1),
    /**
     * Photo-prompt subject grouping the field PWA uses (photo-prompts.ts /
     * photo-naming.ts STANDARD_PHOTO_SUBJECTS). Display-oriented — presence
     * detection goes through `promptKeys`.
     */
    subject: z.string().min(1),
    /** Every promptKey that satisfies this mandatory shot (exact match). */
    promptKeys: z.array(z.string().min(1)).min(1),
    /**
     * Checklist sections this shot is mandatory for. An entry only ever lists
     * a section whose prompt set (or clause book) can actually produce it.
     */
    appliesTo: z.array(ChecklistSectionSchema).min(1),
});
const BOTH_BATTERY_SECTIONS = [
    'battery-section-4-ac',
    'battery-section-5-dc',
];
/** A checklist-referenced mandatory shot also carries its item + clauses. */
export const MandatoryChecklistPhotoSchema = MandatoryPhotoSchema.extend({
    /** e.g. "SB & Labelling 16" — matches ClauseResponse.clauseId / the assessment library. */
    checklistItemId: z.string().min(1),
    /** Standard clauses the shot evidences. */
    clauses: z.array(z.string().min(1)).min(1),
});
/**
 * The mandatory HARDWARE shots, per section.
 *
 * promptKey mapping notes:
 *   - `switchboard` is the battery-side Switchboard prompt (photo-prompts.ts
 *     adds it as an addendum — the May 2026 photo-list docx omits it, but
 *     docs/PHOTO_NAMING.md carries the Switchboard standard subject).
 *   - "spec label" maps to the battery system label+serial prompt; the
 *     "data label/serial" maps to the module labels+serials prompt. Both are
 *     stand-back (legible, in context) shots per the photo list docx.
 *   - The PV entries mirror the same evidence intent onto the PV prompt
 *     library (switchboard, inverter label+serial, module label+serial) —
 *     all five are already `required: true` prompts the PWA blocks submit on,
 *     so this adds no new burden on the inspector.
 *   - Section 4 (integrated BESS) has NO inverter prompt: the prompt library
 *     carries zero `integrated-bs` entries because the Section 4 photo docx
 *     has not been parsed yet. Add the inverter shot to its scope once it is.
 */
export const MANDATORY_HARDWARE_PHOTOS = [
    {
        key: 'switchboard',
        label: 'Switchboard',
        subject: 'Switchboard',
        promptKeys: ['switchboard'],
        appliesTo: [...BOTH_BATTERY_SECTIONS],
    },
    {
        key: 'battery-spec-label',
        label: 'Battery spec label (stand-back)',
        subject: 'Battery system',
        promptKeys: ['battery-system-battery-system-label-and-serial-number'],
        appliesTo: [...BOTH_BATTERY_SECTIONS],
    },
    {
        key: 'battery-data-label-serial',
        label: 'Battery data label / serial (stand-back)',
        subject: 'Battery system',
        promptKeys: ['battery-system-battery-modules-labels-and-serial-numbers'],
        appliesTo: [...BOTH_BATTERY_SECTIONS],
    },
    {
        key: 'inverter-spec-label-serial',
        label: 'Inverter spec label with serial',
        subject: 'Inverter',
        promptKeys: ['inverter-inverter-label-and-serial-number'],
        appliesTo: ['battery-section-5-dc'],
    },
    {
        key: 'pv-switchboard',
        label: 'Switchboard — main switch inverter supply',
        subject: 'Switchboard',
        promptKeys: ['pv-main-switch-inverter-supply'],
        appliesTo: ['cer-pv'],
    },
    {
        key: 'pv-inverter-model-details',
        label: 'Inverter model details',
        subject: 'Inverter',
        promptKeys: ['pv-inverter-model-details'],
        appliesTo: ['cer-pv'],
    },
    {
        key: 'pv-module-model-details',
        label: 'Module make and model (close-up)',
        subject: 'Solar panels',
        promptKeys: ['pv-module-model-details'],
        appliesTo: ['cer-pv'],
    },
    {
        key: 'pv-module-serial',
        label: 'Module serial number (close-up)',
        subject: 'Solar panels',
        promptKeys: ['pv-module-serial'],
        appliesTo: ['cer-pv'],
    },
];
/**
 * The mandatory CHECKLIST-referenced shots. Satisfied by either the
 * `clause:<checklistItemId>` evidence photo or the matching standard prompt.
 *
 * All are battery-side. The `SB & Labelling` items exist in both battery
 * clause books; `Pre-assembled BS 20` only exists in Section 5, so on Section 4
 * the same AS/NZS 5139 CL 7.9 signage is evidenced by the standard prompt.
 * The CER PV checklist has no equivalent mandatory-photo items.
 */
export const MANDATORY_CHECKLIST_PHOTOS = [
    // The mandatory ES-sign shot belongs to item 2, not item 1: item 1 asks
    // whether the sign is INSTALLED and visible to approaching emergency
    // workers, item 2 whether it is ≥100mm with the UN chemistry number below
    // the lettering — which is what a photo actually evidences. Corrected on
    // the technical head's direction (Aug 2026); it shipped against item 1, so
    // reports captured before the correction carry the shot as
    // `clause:SB & Labelling 1`. Both keys stay listed so those captures keep
    // satisfying the requirement — drop the old key and every pre-correction
    // report re-fails QA on a shot it actually has.
    {
        key: 'sb-labelling-2',
        label: 'ES sign — ≥100mm with UN chemistry number',
        checklistItemId: 'SB & Labelling 2',
        clauses: ['AS/NZS 5139:2019 CL7.3', 'Appendix B CLB1'],
        subject: 'Switchboard',
        promptKeys: ['clause:SB & Labelling 2', 'clause:SB & Labelling 1'],
        appliesTo: [...BOTH_BATTERY_SECTIONS],
    },
    {
        key: 'sb-labelling-16',
        label: 'Multimode inverter alternative-supply warning label',
        checklistItemId: 'SB & Labelling 16',
        clauses: ['AS/NZS 4777.1:2024 Clause 6.8'],
        subject: 'Switchboard',
        promptKeys: ['clause:SB & Labelling 16'],
        appliesTo: [...BOTH_BATTERY_SECTIONS],
    },
    {
        key: 'sb-labelling-17',
        label: '"Multiple Supplies" switchboard label',
        checklistItemId: 'SB & Labelling 17',
        clauses: ['AS/NZS 4777.1:2024 Clause 6.3 (g)'],
        subject: 'Switchboard',
        promptKeys: ['clause:SB & Labelling 17'],
        appliesTo: [...BOTH_BATTERY_SECTIONS],
    },
    {
        key: 'sb-labelling-19',
        label: 'Backed-up circuit labelling',
        checklistItemId: 'SB & Labelling 19',
        clauses: ['AS/NZS 3000 Clause 2.10.5.2', 'AS/NZS 4777.1:2024 Clause 5.3.6.1'],
        subject: 'Switchboard',
        promptKeys: ['clause:SB & Labelling 19'],
        appliesTo: [...BOTH_BATTERY_SECTIONS],
    },
    // The site-specific shutdown procedure, AS/NZS 5139 CL 7.16. Requested by
    // the technical head (Aug 2026) — the procedure must be installed adjacent
    // to the system, so a photo is the only proof it is actually there. Each
    // battery book numbers the same requirement differently.
    {
        key: 'pre-assembled-bs-16',
        label: 'Site-specific shutdown procedure',
        checklistItemId: 'Pre-assembled BS 16',
        clauses: ['AS/NZS 5139:2019 CL 7.16'],
        subject: 'Battery system',
        promptKeys: ['clause:Pre-assembled BS 16'],
        appliesTo: ['battery-section-5-dc'],
    },
    {
        key: 'integrated-bess-14',
        label: 'Site-specific shutdown procedure',
        checklistItemId: 'Integrated BESS 14',
        clauses: ['AS/NZS 5139:2019 CL 7.16'],
        subject: 'Battery system',
        promptKeys: ['clause:Integrated BESS 14'],
        appliesTo: ['battery-section-4-ac'],
    },
    {
        key: 'pre-assembled-bs-20',
        label: '"Danger, toxic fumes" signage',
        checklistItemId: 'Pre-assembled BS 20',
        clauses: ['AS/NZS 5139:2019 CL 7.9'],
        subject: 'Battery system',
        promptKeys: ['clause:Pre-assembled BS 20', 'battery-system-battery-danger-toxic-fumes-signage'],
        appliesTo: ['battery-section-5-dc'],
    },
];
/**
 * The reserved promptKey for a photo that has been imported but not yet filed
 * against a prompt or a clause.
 *
 * A first-class state, not a missing one: an unassigned photo satisfies no
 * mandatory shot, which every existing consumer already handles correctly.
 * No entry in the photo-prompt library may ever use this key.
 */
export const UNASSIGNED_PROMPT_KEY = 'unassigned';
/** Whether a captured photo's promptKey satisfies a mandatory shot. */
export function mandatoryPhotoSatisfiedBy(entry, promptKey) {
    const key = promptKey.trim();
    const canonical = canonicalPromptKey(key);
    return entry.promptKeys.includes(key) || entry.promptKeys.includes(canonical);
}
/**
 * Whether `promptKey` is a legal filing target for a job.
 *
 * Three legal shapes: the reserved unassigned key, a key from the job kind's
 * prompt library, or `clause:<id>` naming a clause in one of the job's own
 * checklist sections.
 *
 * Enforced server-side on every re-file. An unvalidated key is worse than a
 * rejected one: it satisfies no mandatory shot and no clause, so the photo
 * silently vanishes from every completeness check and resurfaces later as a QA
 * failure the inspector cannot account for.
 *
 * Takes the prompt and clause lookups as arguments rather than importing them,
 * to keep this module free of a cycle with photo-prompts/clause-library.
 */
export function isAssignablePromptKey(promptKey, context) {
    const key = promptKey.trim();
    if (!key)
        return false;
    if (key === UNASSIGNED_PROMPT_KEY)
        return true;
    if (canonicalPromptKey(key) !== key && context.promptKeys.includes(canonicalPromptKey(key)))
        return true;
    if (key === GENERAL_RECOMMENDATIONS_PHOTO_PROMPT_KEY)
        return true;
    if (context.promptKeys.includes(key))
        return true;
    if (key.startsWith('clause:')) {
        return context.clauseIdsBySection.includes(key.slice('clause:'.length).trim());
    }
    return false;
}
function scopedTo(entries, sections) {
    return entries.filter((entry) => entry.appliesTo.some((s) => sections.includes(s)));
}
/**
 * The hardware shots mandatory for a submission covering `sections`.
 * Pass every section the submission actually answered — a combo job covers
 * both the PV and a battery book, and must carry both sets.
 */
export function mandatoryHardwarePhotosFor(sections) {
    return scopedTo(MANDATORY_HARDWARE_PHOTOS, sections);
}
/** The checklist-referenced shots mandatory for a submission covering `sections`. */
export function mandatoryChecklistPhotosFor(sections) {
    return scopedTo(MANDATORY_CHECKLIST_PHOTOS, sections);
}
/**
 * Which checklist sections a submission actually covers — the input to the
 * two selectors above.
 *
 * The answered responses are the truth: a combo job answers both books, a
 * Section 4 job never touches `Pre-assembled BS`. A documentation-only
 * sibling book (the old docs-not-sighted stamp) is not coverage. The JobID
 * prefix is only a fallback for a submission with no responses at all
 * (which fails QA on other grounds anyway); empty BTY resolves to both
 * battery books because the prefix cannot distinguish Section 4 from 5.
 */
export function sectionsCoveredBy(submission) {
    const answered = [...new Set(submission.checklist.map((r) => r.section))];
    if (answered.length === 0) {
        return submission.jobId.toUpperCase().startsWith('PVD')
            ? ['cer-pv']
            : [...BOTH_BATTERY_SECTIONS];
    }
    // Historic documentation stamps alone must not turn a PV inspection into
    // battery work. Keep substantive battery answers, including combo reports.
    const pvWork = submission.checklist.some((row) => row.section === 'cer-pv' && !isDocumentationClause(row.clauseId));
    const scoped = submission.jobId.toUpperCase().startsWith('PVD') && pvWork
        ? answered.filter((section) => section === 'cer-pv' ||
            submission.checklist.some((row) => row.section === section && !isDocumentationClause(row.clauseId)))
        : answered;
    return dropDocumentationOnlySiblingBatteryBook(scoped, submission.checklist);
}
function isDocumentationClause(clauseId) {
    return clauseId.trim().toLowerCase().startsWith('documentation');
}
/**
 * Docs-not-sighted used to stamp Documentation 1 onto every applicable book.
 * A Section 5 inspection that also carried that stamp on Section 4 then
 * looked like it covered both books. Drop a battery book that only has
 * documentation rows when the sibling book has real clause work.
 */
function dropDocumentationOnlySiblingBatteryBook(answered, checklist) {
    const battery = answered.filter((section) => BOTH_BATTERY_SECTIONS.includes(section));
    if (battery.length < 2)
        return answered;
    const hasRealWork = (section) => checklist.some((row) => row.section === section && !isDocumentationClause(row.clauseId));
    const section4Real = hasRealWork('battery-section-4-ac');
    const section5Real = hasRealWork('battery-section-5-dc');
    if (section4Real === section5Real)
        return answered;
    return answered.filter((section) => {
        if (section === 'battery-section-4-ac')
            return section4Real;
        if (section === 'battery-section-5-dc')
            return section5Real;
        return true;
    });
}
//# sourceMappingURL=mandatory-photos.js.map